import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Card } from "shared/components";
import { GuestlistBlockForm } from "Bands/components";
import { copy } from "shared/assets";
import {
  BAND_ACTIONS,
  EventBlocks,
  IGuestlistBlock,
  IGuestlistBlockParams,
  IGuestlistBlockStatus,
} from "Bands/types";
import { IAPIState, IFormButtons, IStatus, ITheme } from "shared/types";
import { Guests } from "shared/assets/icons";
import {
  clearActionMeta,
  editEventGuestlistBlock,
  dataMetaSelector,
} from "Bands/state";
import { useEventContext } from "Bands/contexts";
import {
  useClearActionMetaOnUnMount,
  useEventRoute,
  useGuestlistNumberError,
} from "Bands/utils";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import { BAND_PATHS } from "Bands/router";
import { EditTicketAndPasses } from "./EditTicketAndPasses";
import CopyBlockModal from "Bands/pages/copy-block/CopyBlockModal";

const editCopy = copy.guestlistBlock.edit;

interface EditGuestlistBlockFormProps {
  block: IGuestlistBlock;
}

export const EditGuestlistBlockForm: React.FC<EditGuestlistBlockFormProps> = ({
  block: {
    id,
    title,
    status,
    guestBlockType,
    maxTicketsPerEntry,
    ticketAndPassTypes,
  },
}) => {
  const dispatch = useDispatch();
  const { eventId } = useEventContext();
  const { pushRoute } = useEventRoute();

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editEventGuestlistBlock)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.editEventGuestlistBlock);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.editEventGuestlistBlock));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.messages.success,
        })
      );
    }
  }, [apiMeta?.status, dispatch, eventId, pushRoute]);

  const defaultValues: IGuestlistBlockParams = useMemo(
    () => ({
      title: title,
      status: status,
      guestBlockType: guestBlockType,
      maxTicketsPerEntry: maxTicketsPerEntry,
    }),
    [guestBlockType, maxTicketsPerEntry, status, title]
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        size: "m",
        "data-testid": "guestlist_block_submit",
      },
    }),
    []
  );

  const handleSubmit = (data: IGuestlistBlockParams) => {
    dispatch(
      editEventGuestlistBlock({
        id,
        data,
      })
    );
  };

  const { error } = useGuestlistNumberError(apiMeta?.error);

  const handleFinishEditing = useCallback(
    () => pushRoute(BAND_PATHS.BAND_EVENT),
    [pushRoute]
  );

  return (
    <Card icon={Guests} title={editCopy.title.replace("%title", title)}>
      <Spacer size="m">
        <CopyBlockModal blockType={EventBlocks.guestlist} deleteBlockId={id} />
        <GuestlistBlockForm
          submitMode="isDirty"
          defaultValues={defaultValues}
          buttons={buttons}
          handleSubmit={handleSubmit}
          apiStatus={apiMeta?.status}
          apiError={apiMeta?.error}
          error={error}
          disableType={status === IGuestlistBlockStatus.closed}
          disableNbTickets={status === IGuestlistBlockStatus.closed}
          hideTitle
        />
        <EditTicketAndPasses
          blockId={id}
          ticketAndPasses={ticketAndPassTypes}
          isGuestlistClosed={status === IGuestlistBlockStatus.closed}
        />
        <Button onClick={handleFinishEditing} theme={ITheme.secondary}>
          {copy.form.finish}
        </Button>
      </Spacer>
    </Card>
  );
};
